import * as A from '@core/arena'

import { Dates } from '@openforis/arena-core'

import * as ObjectUtils from '@core/objectUtils'
import * as Validation from '@core/validation/validation'

import { ChainSamplingDesign } from './chainSamplingDesign'
import { ChainStatisticalAnalysis } from './chainStatisticalAnalysis'

export const keys = {
  dateCreated: ObjectUtils.keys.dateCreated,
  dateExecuted: 'dateExecuted',
  dateModified: ObjectUtils.keys.dateModified,
  props: ObjectUtils.keys.props,
  statusExec: 'statusExec',
  uuid: ObjectUtils.keys.uuid,
  temporary: ObjectUtils.keys.temporary,
  validation: Validation.keys.validation,
  scriptCommon: 'scriptCommon',
  scriptEnd: 'scriptEnd',
}

export const keysProps = {
  labels: ObjectUtils.keysProps.labels,
  descriptions: ObjectUtils.keysProps.descriptions,
  hasSamplingDesign: 'hasSamplingDesign',
  samplingDesign: 'samplingDesign',
  analysisNodeDefs: 'analysisNodeDefs',
  submitOnlyAnalysisStepDataIntoR: 'submitOnlyAnalysisStepDataIntoR',
  submitOnlySelectedRecordsIntoR: 'submitOnlySelectedRecordsIntoR',
  selectedRecordUuids: 'selectedRecordUuids',
  statisticalAnalysis: 'statisticalAnalysis',
  resultsBackFromRStudio: 'resultsBackFromRStudio',
  includeEntitiesWithoutData: 'includeEntitiesWithoutData',
}

export const statusExec = {
  error: 'error',
  success: 'success',
  running: 'running',
}

// ====== READ

export const {
  getUuid,
  getProps,
  getPropsDiff,
  getDateCreated,
  getDateModified,
  getDescriptions,
  getDescription,
  getLabels,
  getLabel,
  isTemporary,
} = ObjectUtils
export const getDateExecuted = ObjectUtils.getDate(keys.dateExecuted)
export const getStatusExec = A.propOr(null, keys.statusExec)
export const getScriptCommon = A.propOr(null, keys.scriptCommon)
export const getScriptEnd = A.propOr(null, keys.scriptEnd)
export const hasSamplingDesign = ObjectUtils.isPropTrue(keysProps.hasSamplingDesign)
export const isSubmitOnlyAnalysisStepDataIntoR = ObjectUtils.isPropTrue(keysProps.submitOnlyAnalysisStepDataIntoR)
export const isSubmitOnlySelectedRecordsIntoR = ObjectUtils.isPropTrue(keysProps.submitOnlySelectedRecordsIntoR)
export const getSelectedRecordUuids = ObjectUtils.getProp(keysProps.selectedRecordUuids, [])
export const getSamplingDesign = ObjectUtils.getProp(keysProps.samplingDesign, {})
export const getStatisticalAnalysis = ObjectUtils.getProp(keysProps.statisticalAnalysis, {})
export const isResultsBackFromRStudio = ObjectUtils.getProp(keysProps.resultsBackFromRStudio, true)
export const isIncludeEntitiesWithoutData = ObjectUtils.getProp(keysProps.includeEntitiesWithoutData, false)

// ====== UPDATE
export const assocHasSamplingDesign = (value) => ObjectUtils.setProp(keysProps.hasSamplingDesign, value)

export const assocSubmitOnlyAnalysisStepDataIntoR = (value) =>
  ObjectUtils.setProp(keysProps.submitOnlyAnalysisStepDataIntoR, value)

export const assocSubmitOnlySelectedRecordsIntoR = (value) => (chain) => {
  let chainUpdated = ObjectUtils.setProp(keysProps.submitOnlySelectedRecordsIntoR, value)(chain)
  if (!value) {
    chainUpdated = ObjectUtils.dissocProp(keysProps.selectedRecordUuids)(chainUpdated)
  }
  return chainUpdated
}

export const assocSelectedRecordUuids = (selectedRecordUuids) =>
  ObjectUtils.setProp(keysProps.selectedRecordUuids, selectedRecordUuids)

export const assocResultsBackFromRStudio = (value) => ObjectUtils.setProp(keysProps.resultsBackFromRStudio, value)

export const assocIncludeEntitiesWithoutData = (value) =>
  ObjectUtils.setProp(keysProps.includeEntitiesWithoutData, value)

const assocSamplingDesign = (value) => ObjectUtils.setProp(keysProps.samplingDesign, value)

export const updateSamplingDesign = (updateFn) => (chain) => {
  const samplingDesign = getSamplingDesign(chain)
  const samplingDesignUpdated = updateFn(samplingDesign)
  let chainUpdated = assocSamplingDesign(samplingDesignUpdated)(chain)
  if (
    !ChainSamplingDesign.getSamplingStrategy(samplingDesignUpdated) &&
    ChainStatisticalAnalysis.getPValue(getStatisticalAnalysis(chainUpdated))
  ) {
    chainUpdated = updateStatisticalAnalysis(ChainStatisticalAnalysis.resetPValue)(chainUpdated)
  }
  if (
    !ChainSamplingDesign.getClusteringNodeDefUuid(samplingDesign) &&
    ChainStatisticalAnalysis.isClusteringOnlyVariances(getStatisticalAnalysis(chainUpdated))
  ) {
    chainUpdated = updateStatisticalAnalysis(ChainStatisticalAnalysis.assocClusteringOnlyVariances(false))(chainUpdated)
  }
  return chainUpdated
}

const assocStatisticalAnalysis = (value) => ObjectUtils.setProp(keysProps.statisticalAnalysis, value)

export const updateStatisticalAnalysis = (updateFn) => (chain) => {
  const statisticalAnalysis = getStatisticalAnalysis(chain)
  const getStatisticalAnalysisUpdated = updateFn(statisticalAnalysis)
  return assocStatisticalAnalysis(getStatisticalAnalysisUpdated)(chain)
}

// ====== CHECK
export const isDraft = A.ifElse(A.pipe(getDateExecuted, A.isNil), A.always(true), (chain) =>
  Dates.isAfter(getDateModified(chain), getDateExecuted(chain))
)

export const checkChangeRequiresSurveyPublish = ({ chainPrev, chainNext }) => {
  const samplingDesignPrev = getSamplingDesign(chainPrev)
  const samplingDesignNext = getSamplingDesign(chainNext)
  return (
    ChainSamplingDesign.getBaseUnitNodeDefUuid(samplingDesignPrev) !==
    ChainSamplingDesign.getBaseUnitNodeDefUuid(samplingDesignNext)
  )
}

// ====== VALIDATION
// The validation object contains the validation of chain index by uuids
export const { getValidation, hasValidation, assocValidation, dissocValidation } = Validation

export const getItemValidationByUuid = (uuid) => A.pipe(getValidation, Validation.getFieldValidation(uuid))
export const assocItemValidation = (uuid, validation) => (chain) =>
  A.pipe(getValidation, Validation.assocFieldValidation(uuid, validation), (validationUpdated) =>
    Validation.assocValidation(validationUpdated)(chain)
  )(chain)
